import { type ProjectTypeTemplatePhase } from "../../../Templates/types/project-type.types.ts"
import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"
import { DRAFT_MILESTONE_PREFIX } from "./lead-scope-milestones.ts"

interface SelectedProjectType {
  value: string
  phases?: ProjectTypeTemplatePhase[]
}

const seedMilestone = (
  phase: ProjectTypeTemplatePhase,
  projectTypeId: string
): LeadFormDataPhase => ({
  id: `${DRAFT_MILESTONE_PREFIX}${phase?.id}`,
  name: phase?.name ?? "",
  description: phase?.description,
  order: phase?.order ?? 0,
  is_enabled: phase?.is_enabled,
  project_type: phase?.project_type || projectTypeId,
  budget: { budget: phase?.default_budget || 0 },
  duration_days: phase?.default_duration_days,
  checklist_data: phase?.checklist_items_primitive ?? []
})

/**
 * A type's template is consulted only the first time it is selected: once the
 * lead holds a row for that type, the whole bucket is kept verbatim, so a row
 * the user renamed, repriced or deleted is never resurrected. Dropping a type
 * drops its rows with it.
 *
 * `preserveExisting` answers the lead that reaches its first project type with
 * milestones already entered by hand — seeding would throw them away.
 */
const resolveProjectPhasesOnTypeChange = (
  selectedTypes: SelectedProjectType[],
  currentPhases: LeadFormDataPhase[],
  preserveExisting: boolean
): LeadFormDataPhase[] => {
  if (preserveExisting && currentPhases.length) return currentPhases

  const phasesById = new Map(currentPhases.map((phase) => [phase?.id, phase]))

  const phasesByProjectType = new Map<string, LeadFormDataPhase[]>()
  for (const phase of currentPhases) {
    if (!phase?.project_type) continue
    const bucket = phasesByProjectType.get(phase.project_type) ?? []
    bucket.push(phase)
    phasesByProjectType.set(phase.project_type, bucket)
  }

  return selectedTypes.flatMap((type) => {
    const kept = phasesByProjectType.get(type?.value)
    if (kept?.length) return kept

    return (type?.phases ?? []).map(
      (phase) => phasesById.get(phase?.id) ?? seedMilestone(phase, type?.value)
    )
  })
}

/** A type whose bucket is already populated never needs its template read. */
const projectTypesNeedingTemplates = (
  selectedIds: string[],
  currentPhases: LeadFormDataPhase[]
): string[] => {
  const populated = new Set(
    currentPhases
      .filter((phase) => phase?.project_type)
      .map((phase) => phase.project_type)
  )

  return selectedIds.filter((id) => !populated.has(id))
}

export {
  type SelectedProjectType,
  resolveProjectPhasesOnTypeChange,
  projectTypesNeedingTemplates
}
